import { ActionIcon, Box, Checkbox, Group, Table as MantineTable, Text, Tooltip, UnstyledButton } from "@mantine/core"
import { CaretDownIcon, CaretUpDownIcon, CaretUpIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { Sort } from "../table"
import { ReasonTooltip } from "../ui"
import classes from "./list-table.module.css"

/** One column of a list built on CustomsTable. `label` is already translated. */
export type ListColumn<R> = {
  id: string
  label: string
  /** Backend sort key; columns without one are not sortable. */
  sort?: string
  align?: "right"
  /** Hidden by default, switched on from the «Багана» menu. */
  optional?: boolean
  render: (record: R, index: number) => ReactNode
}

type HeadProps<R> = {
  columns: ListColumn<R>[]
  actionLabel: string
  sorted: [string, Sort]
  onSort: (key: string) => void
  /** Leading checkbox column (bulk selection). */
  select?: { label: string; checked: boolean; indeterminate: boolean; disabled: boolean; onChange: () => void }
}

export function ListHead<R>({ columns, actionLabel, sorted, onSort, select }: HeadProps<R>) {
  return (
    <MantineTable.Thead>
      <MantineTable.Tr>
        {select && (
          <MantineTable.Th w={44}>
            <Checkbox aria-label={select.label} checked={select.checked} indeterminate={select.indeterminate} disabled={select.disabled} onChange={select.onChange} />
          </MantineTable.Th>
        )}
        <MantineTable.Th>{actionLabel}</MantineTable.Th>
        {columns.map((c) => {
          const active = sorted[0] === c.sort
          const SortIcon = active ? (sorted[1] === Sort.ASC ? CaretUpIcon : CaretDownIcon) : CaretUpDownIcon
          return (
            <MantineTable.Th key={c.id} ta={c.align === "right" ? "right" : undefined} aria-sort={active ? (sorted[1] === Sort.ASC ? "ascending" : "descending") : undefined}>
              {c.sort ? (
                <UnstyledButton onClick={() => onSort(c.sort!)} className={classes.sortButton} data-align={c.align}>
                  <Text span inherit>{c.label}</Text>
                  <Box component="span" className={classes.sortIcon} data-active={active || undefined}>
                    <SortIcon size={14} className={active ? undefined : "sort-idle"} />
                  </Box>
                </UnstyledButton>
              ) : (
                c.label
              )}
            </MantineTable.Th>
          )
        })}
      </MantineTable.Tr>
    </MantineTable.Thead>
  )
}

/** Toggle a sort key: a new key sorts ascending, the same key flips. */
export function nextSort(state: [string, Sort], key: string): [string, Sort] {
  if (state[0] !== key) return [key, Sort.ASC]
  return [key, state[1] === Sort.ASC ? Sort.DESC : Sort.ASC]
}

export type RowActionItem = {
  key: string
  label: string
  icon: ReactNode
  /** Permission or status reason; null when the action is available. */
  reason: string | null
  onClick: () => void
}

/**
 * Row actions live in the first column as grey subtle icon buttons (no 3-dot menu). An action that
 * exists for the row but is not permitted stays visible, aria-disabled, with the reason.
 */
export function RowActionIcons({ items }: { items: RowActionItem[] }) {
  return (
    <Group gap="xs" wrap="nowrap">
      {items.map((item) => {
        const button = (
          <ActionIcon
            variant="light"
            color="gray"
            size={32}
            radius="md"
            className={classes.rowAction}
            aria-label={item.label}
            onClick={(e) => {
              e.stopPropagation()
              item.onClick()
            }}
          >
            {item.icon}
          </ActionIcon>
        )
        return item.reason ? (
          <ReasonTooltip key={item.key} reason={`${item.label}. ${item.reason}`}>{button}</ReasonTooltip>
        ) : (
          <Tooltip key={item.key} label={item.label} position="bottom" withArrow openDelay={150}>{button}</Tooltip>
        )
      })}
    </Group>
  )
}
